/* ============================================================
   custom.pdp.atc-stitch.js — the ADD TO CART button's stitched outline,
   and the revolution it makes when you add something. 31 Jul 2026

   WHY THERE IS AN SVG HERE AT ALL. The stitching started as a dashed border,
   which was right until it had to MOVE: a border's dashes are painted by the
   engine and nothing about them is animatable. Travelling stitches need a
   stroke — stroke-dashoffset is the one property that makes a dash pattern
   walk — so the outline is a real <svg><rect>, the same construction as the
   masthead's search pill (sections/custom-header.liquid).

   AND WHY IT IS INJECTED RATHER THAN AUTHORED. The button is Horizon's, inside
   a block this theme does not own, and the whole product form is re-rendered
   through the Section Rendering API on every variant change. There is no
   Liquid seam to put it in that survives that. Same answer as
   custom.pdp.variant-select.js: inject, and keep an observer to put it back.

   WHAT REPLACED WHAT. Horizon plays a checkmark-burst inside the button on a
   successful add — white, on a white button, so it reads as a flicker (Devin,
   31 Jul: "there's an animation that plays but it's white so you can't really
   see it"). That is hidden in custom.pdp.buybar.css and this is the
   replacement: one full revolution of the stitching, which is the same
   vocabulary as the search pill's half-turn on focus.

   THE 673 IS NOT A PIXEL COUNT. The rect is authored pathLength="673", so the
   browser scales the dash pattern to make the perimeter exactly 673 units
   whatever the button's real size. One full turn is therefore always 673, at
   every breakpoint, and the CSS keyframe can hard-code it.
   ============================================================ */
(function () {
  'use strict';

  var WRAP = '.product-details .product-form-buttons > span:has(.add-to-cart-button)';
  var OUTLINE_CLASS = 'ffg-atc-stitch';
  var SEWING_CLASS = 'is-sewing';
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function outlineFor(wrap) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', OUTLINE_CLASS);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    var rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('width', '100%');
    rect.setAttribute('height', '100%');
    /* Matches the button's own radius plus the 5px the outline stands off it,
       so the thread runs parallel to the edge rather than cutting its corners.
       Button radius is 9px in custom.pdp.buybar.css. */
    rect.setAttribute('rx', '14');
    /* NO pathLength (16 Aug, Devin: the stitches stretched on the jumbo
       double-row ADD TO CART). pathLength normalised the perimeter to a fixed
       count, so the same number of dashes spread over a bigger button = longer
       dashes. Without it the dash-array is read in real px, so the stitch size
       stays constant and a bigger button simply gets MORE stitches. */
    svg.appendChild(rect);

    wrap.appendChild(svg);
    return svg;
  }

  function sew(wrap) {
    /* Restart even mid-run: dropping the class, forcing a reflow and adding it
       back is the only way to replay a CSS animation on an element that is
       already running one. Adding to cart twice in a row should stitch twice,
       not stall on the first. */
    wrap.classList.remove(SEWING_CLASS);
    void wrap.offsetWidth;
    wrap.classList.add(SEWING_CLASS);
  }

  function bind(wrap) {
    if (!wrap.querySelector('.' + OUTLINE_CLASS)) outlineFor(wrap);
    if (wrap.dataset.ffgSewBound) return;
    wrap.dataset.ffgSewBound = '1';

    /* On the WRAPPER, not the button: the button is replaced wholesale by the
       re-render and a listener on it would go with it, while this element is
       ours and outlives that. Capture phase so it still fires if Horizon's own
       handler stops propagation. */
    wrap.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest && event.target.closest('.add-to-cart-button');
        if (!button || button.disabled) return;
        sew(wrap);
      },
      true
    );

    wrap.addEventListener('animationend', function () {
      wrap.classList.remove(SEWING_CLASS);
    });
  }

  /* querySelectorAll(':has(...)') throws a synchronous SyntaxError on engines
     without :has (older Android Chrome/Firefox, some WebViews), which would
     kill init on those browsers. Try the real selector; on throw, fall back to
     the buttons themselves mapped to the direct-child <span> of
     .product-form-buttons that wraps each. Modern engines (all current
     Mac/iOS Safari, Chrome, Firefox) take the try path unchanged. */
  function selectWraps() {
    try {
      return document.querySelectorAll(WRAP);
    } catch (e) {
      var out = [];
      var btns = document.querySelectorAll(
        '.product-details .product-form-buttons .add-to-cart-button'
      );
      Array.prototype.forEach.call(btns, function (btn) {
        var el = btn.parentElement;
        while (el && el.parentElement) {
          if (
            el.tagName === 'SPAN' &&
            el.parentElement.classList.contains('product-form-buttons')
          ) {
            if (out.indexOf(el) === -1) out.push(el);
            break;
          }
          el = el.parentElement;
        }
      });
      return out;
    }
  }

  function syncAll() {
    Array.prototype.forEach.call(selectWraps(), bind);
  }

  var observer = null;

  function observe() {
    if (!window.MutationObserver) return;
    var scope = document.querySelector('[id^="ProductInformation-"]');
    var host = (scope && scope.closest('[id^="shopify-section-"]')) || document.body;
    /* This writes into the tree it watches, so the insert re-triggers the
       callback — it cannot loop, because the second pass finds the outline
       already there and the bound flag already set. */
    /* init() runs again per navigation now — disconnect the previous observer
       rather than stacking a new one on top each time. */
    if (observer) observer.disconnect();
    observer = new MutationObserver(syncAll);
    observer.observe(host, { childList: true, subtree: true });
  }

  function init() {
    observe();
    syncAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  /* Loaded on every page since 10 Aug (custom.globals.liquid); no-reload
     navigation replaces the section wrapper the observer holds, so re-arm per
     swap exactly as for the editor's section re-render. */
  document.addEventListener('ffg:navigated', init);
})();
